import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Stage, Layer, Line, Rect, Arrow, Circle, Text, Transformer } from 'react-konva';
import styles from './VideoPlayer.module.scss';

/** Konva bazen getPointerPosition döndürmez; tıklama aynı frame’de odak için DOM koordinatından hesapla */
function pointerStagePos(
  stage: { getPointerPosition: () => { x: number; y: number } | null; width: () => number; height: () => number; container: () => HTMLElement },
  domEvt: { clientX: number; clientY: number } | undefined,
): { x: number; y: number } | null {
  const p = stage.getPointerPosition?.();
  if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) {
    return p;
  }
  if (!domEvt || typeof domEvt.clientX !== 'number') return null;
  try {
    const container = stage.container();
    const rect = container.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const sx = stage.width() / rect.width;
    const sy = stage.height() / rect.height;
    return {
      x: (domEvt.clientX - rect.left) * sx,
      y: (domEvt.clientY - rect.top) * sy,
    };
  } catch {
    return null;
  }
}

export type DrawingTool =
  | 'line'
  | 'rect'
  | 'arrow'
  | 'circle'
  | 'text'
  | 'select'
  | 'none';

export interface Shape {
  id: string;
  tool: DrawingTool;
  points: number[];
  color: string;
  text?: string;
  /** Derece; Transformer döndürme — props’ta yoksa Konva 0’a çeker */
  rotation?: number;
  /** Metin aracı: ölçekleme sonrası (varsayılan 24) */
  fontSize?: number;
}

function konvaTypeName(node: any): string {
  if (!node) return '';
  return typeof node.getClassName === 'function' ? node.getClassName() : String(node.className ?? '');
}

/** Döndürme/ölçek tutamaçları Transformer altında; tek seviye parent kontrolü yetmez */
function isInsideTransformer(node: any): boolean {
  let n = node;
  while (n) {
    const t = konvaTypeName(n);
    if (t === 'Transformer') return true;
    if (t === 'Stage') return false;
    n = n.getParent?.();
  }
  return false;
}

interface DrawingCanvasProps {
  stageSize: { width: number; height: number };
  shapes: Shape[];
  tool: DrawingTool;
  color: string;
  selectedShapeId?: string | null;
  isDrawingEnabled: boolean;
  onShapesChange: (shapes: Shape[]) => void;
  onTextToolClick: (pos: { x: number, y: number }) => void;
  onShapeSelection?: (shapeId: string | null) => void;
  onDrawingComplete?: () => void;
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  stageSize,
  shapes,
  tool,
  color,
  selectedShapeId,
  isDrawingEnabled,
  onShapesChange,
  onTextToolClick,
  onShapeSelection,
  onDrawingComplete,
}) => {
  const drawingInteractionActive = isDrawingEnabled && tool !== 'none';
  const transformerRef = useRef<any>(null);
  const selectedShapeRef = useRef<any>(null);
  const isDrawingRef = useRef(false);
  const currentShapeIdRef = useRef<string | null>(null);
  const textInputRef = useRef<HTMLTextAreaElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [inlineTextDraft, setInlineTextDraft] = useState('');
  /** Konva stage içi koordinat (normalize commit için) */
  const [inlineTextStagePos, setInlineTextStagePos] = useState<{ x: number; y: number } | null>(
    null,
  );

  // Yardımcı fonksiyonlar
  function normalizePoint(x: number, y: number, width: number, height: number) {
    return [x / width, y / height];
  }

  function denormalizePoint(nx: number, ny: number, width: number, height: number) {
    return [nx * width, ny * height];
  }

  function denormalizeShape(shape: Shape, width: number, height: number): Shape {
    const denormPoints = [];
    for (let i = 0; i < shape.points.length; i += 2) {
      const [x, y] = denormalizePoint(shape.points[i], shape.points[i + 1], width, height);
      denormPoints.push(x, y);
    }
    return { ...shape, points: denormPoints };
  }

  const selectedShapeSnapshot = React.useMemo(() => {
    if (!selectedShapeId) return '';
    const s = shapes.find((x) => x.id === selectedShapeId);
    return s ? JSON.stringify(s) : '';
  }, [shapes, selectedShapeId]);

  // Transformer'ı seçili şekle bağla (geometri güncellenince yeniden hizala)
  React.useLayoutEffect(() => {
    if (
      !drawingInteractionActive ||
      tool !== 'select' ||
      !selectedShapeId ||
      !selectedShapeRef.current ||
      !transformerRef.current
    ) {
      return;
    }
    transformerRef.current.nodes([selectedShapeRef.current]);
    transformerRef.current.getLayer()?.batchDraw();
  }, [drawingInteractionActive, tool, selectedShapeId, selectedShapeSnapshot]);

  /* Metin: flushSync sonrası ref bazen bir sonraki mikro görevde — yedek odak */
  React.useLayoutEffect(() => {
    if (!drawingInteractionActive || tool !== 'text' || inlineTextStagePos == null) return;
    const el = textInputRef.current;
    if (!el || document.activeElement === el) return;
    el.focus({ preventScroll: true });
    try {
      el.setSelectionRange(0, 0);
    } catch {
      /* bazı tarayıcılar boş alanda setSelectionRange reddeder */
    }
  }, [drawingInteractionActive, tool, inlineTextStagePos]);

  /** Ölçek/döndürme bittiğinde Konva düğümünden gerçek geometriyi modele yaz */
  const handleTransformEnd = () => {
    if (!drawingInteractionActive || !selectedShapeRef.current || !selectedShapeId) return;
    const node = selectedShapeRef.current;
    const shape = shapes.find((s) => s.id === selectedShapeId);
    if (!shape) return;

    const w = stageSize.width;
    const h = stageSize.height;
    const sx = node.scaleX();
    const sy = node.scaleY();
    let newPoints = [...shape.points];
    let newRotation = node.rotation();
    let newFontSize = shape.fontSize;

    switch (shape.tool) {
      case 'rect': {
        const newWidth = node.width() * sx;
        const newHeight = node.height() * sy;
        node.width(newWidth);
        node.height(newHeight);
        node.scaleX(1);
        node.scaleY(1);
        const x = node.x();
        const y = node.y();
        newPoints = [x / w, y / h, (x + newWidth) / w, (y + newHeight) / h];
        break;
      }
      case 'circle': {
        const newR = node.radius() * Math.max(sx, sy);
        node.radius(newR);
        node.scaleX(1);
        node.scaleY(1);
        const cx = node.x();
        const cy = node.y();
        newPoints = [cx / w, cy / h, (cx + newR) / w, cy / h];
        break;
      }
      case 'line':
      case 'arrow': {
        const abs = node.getAbsoluteTransform();
        const pts: number[] = node.points?.() ?? [];
        newPoints = [];
        for (let i = 0; i < pts.length; i += 2) {
          const p = abs.point({ x: pts[i], y: pts[i + 1] });
          newPoints.push(p.x / w, p.y / h);
        }
        node.scaleX(1);
        node.scaleY(1);
        node.rotation(0);
        node.skewX(0);
        node.skewY(0);
        node.x(0);
        node.y(0);
        node.offsetX(0);
        node.offsetY(0);
        newRotation = 0;
        break;
      }
      case 'text': {
        const baseFs = shape.fontSize ?? 24;
        const fs = Math.max(8, baseFs * Math.max(sx, sy));
        newFontSize = fs;
        if (typeof node.fontSize === 'function') {
          node.fontSize(fs);
        }
        node.scaleX(1);
        node.scaleY(1);
        newPoints = [node.x() / w, node.y() / h];
        break;
      }
      default:
        return;
    }

    onShapesChange(
      shapes.map((s) => {
        if (s.id !== selectedShapeId) return s;
        if (s.tool === 'text') {
          return { ...s, points: newPoints, rotation: newRotation, fontSize: newFontSize ?? 24 };
        }
        return { ...s, points: newPoints, rotation: newRotation };
      }),
    );

    requestAnimationFrame(() => {
      if (selectedShapeRef.current && transformerRef.current) {
        transformerRef.current.nodes([selectedShapeRef.current]);
        transformerRef.current.getLayer()?.batchDraw();
      }
    });
  };

  const getStageFromEvent = (e: { target?: { getStage?: () => any } }) =>
    e.target?.getStage?.() ?? null;

  // Drawing Handlers (pointer: mouse + touch / tablet)
  const handlePointerDown = (e: any) => {
    if (!drawingInteractionActive) return;

    const stage = getStageFromEvent(e);
    if (!stage) return;
    const pos = pointerStagePos(stage, e.evt);
    if (!pos) return;
    e.evt?.preventDefault?.();

    if (tool === 'text') {
      onTextToolClick(pos);
      /* setTimeout ile odak kullanıcı jesti zincirinden çıkar (özellikle Safari); aynı senkron turda odakla */
      flushSync(() => {
        setInlineTextDraft('');
        setInlineTextStagePos({ x: pos.x, y: pos.y });
      });
      const el = textInputRef.current;
      if (el) {
        el.focus({ preventScroll: true });
        try {
          el.setSelectionRange(0, 0);
        } catch {
          /* ignore */
        }
      }
      return;
    }

    if (inlineTextStagePos) {
      setInlineTextStagePos(null);
      setInlineTextDraft('');
    }

    if (tool === 'select') {
      const clickedShape = e.target;

      if (clickedShape && isInsideTransformer(clickedShape)) {
        return;
      }

      if (clickedShape && clickedShape.attrs && clickedShape.attrs.id) {
        onShapeSelection?.(clickedShape.attrs.id);
      } else {
        onShapeSelection?.(null);
      }
      return;
    }

    isDrawingRef.current = true;
    // Normalize et
    const [nx, ny] = normalizePoint(pos.x, pos.y, stageSize.width, stageSize.height);
    const newShape: Shape = {
      id: `shape-${Date.now()}`,
      tool,
      points: [nx, ny, nx, ny],
      color,
    };
    currentShapeIdRef.current = newShape.id;
    onShapesChange([...shapes, newShape]);
  };

  const handlePointerMove = (e: any) => {
    if (!drawingInteractionActive || !isDrawingRef.current) return;

    const stage = getStageFromEvent(e);
    if (!stage) return;
    const pos = pointerStagePos(stage, e.evt);
    if (!pos) return;

    const [nx, ny] = normalizePoint(pos.x, pos.y, stageSize.width, stageSize.height);
    const updatedShapes = shapes.map(shape => {
      if (shape.id === currentShapeIdRef.current) {
        let newPoints = [...shape.points];
        if (shape.tool === 'line') {
          newPoints = [...newPoints, nx, ny];
        } else { // rect, arrow, circle
          newPoints[2] = nx;
          newPoints[3] = ny;
        }
        return { ...shape, points: newPoints };
      }
      return shape;
    });
    onShapesChange(updatedShapes);
  };

  const handlePointerUp = () => {
    if (!drawingInteractionActive) return;
    isDrawingRef.current = false;
    currentShapeIdRef.current = null;
    onDrawingComplete?.();
  };

  const handleShapeClick = (shapeId: string) => {
    if (!drawingInteractionActive) return;
    if (tool === 'select') {
      onShapeSelection?.(shapeId);
    }
  };

  // Drag işlemi sırasında şekli güncelle
  const handleShapeDragMove = (e: any, shapeId: string) => {
    if (!drawingInteractionActive) return;
    const shape = shapes.find((s) => s.id === shapeId);
    // Line/Arrow: Konva x,y ile points birlikte; ara karelerde yanlış yazmamak için sadece dragEnd'de senkron
    if (shape?.tool === 'line' || shape?.tool === 'arrow') return;
    setShapePosition(shapeId, e.target);
  };

  // Drag işlemi bittiğinde şekli güncelle
  const handleShapeDragEnd = (e: any, shapeId: string) => {
    if (!drawingInteractionActive) return;
    setShapePosition(shapeId, e.target);
  };

  /**
   * Konva'da Line/Arrow sürüklenince position() ofset, points() yerel kalır.
   * Mutlak sahne koordinatı = x + points[i] (offset dahil).
   */
  const setShapePosition = (shapeId: string, node: any) => {
    const w = stageSize.width;
    const h = stageSize.height;

    const updatedShapes = shapes.map((shape) => {
      if (shape.id !== shapeId) return shape;

      let newPoints = [...shape.points];
      switch (shape.tool) {
        case 'line':
        case 'arrow': {
          const pts: number[] = node.points?.() ?? [];
          const ox = node.x();
          const oy = node.y();
          newPoints = [];
          for (let i = 0; i < pts.length; i += 2) {
            newPoints.push((ox + pts[i]) / w, (oy + pts[i + 1]) / h);
          }
          node.x(0);
          node.y(0);
          break;
        }
        case 'rect': {
          const x = node.x();
          const y = node.y();
          const nx = x / w;
          const ny = y / h;
          const dx = nx - newPoints[0];
          const dy = ny - newPoints[1];
          newPoints[0] = nx;
          newPoints[1] = ny;
          newPoints[2] += dx;
          newPoints[3] += dy;
          break;
        }
        case 'circle': {
          const x = node.x();
          const y = node.y();
          const nx = x / w;
          const ny = y / h;
          const dxC = nx - newPoints[0];
          const dyC = ny - newPoints[1];
          newPoints[0] = nx;
          newPoints[1] = ny;
          newPoints[2] += dxC;
          newPoints[3] += dyC;
          break;
        }
        case 'text': {
          const x = node.x();
          const y = node.y();
          newPoints[0] = x / w;
          newPoints[1] = y / h;
          break;
        }
        default:
          break;
      }
      return { ...shape, points: newPoints };
    });
    onShapesChange(updatedShapes);
  };

  const renderShape = (shape: Shape) => {
    const isSelected = selectedShapeId === shape.id;
    const isDraggable = tool === 'select' && drawingInteractionActive;
    const denormShape = denormalizeShape(shape, stageSize.width, stageSize.height);
    const rot = shape.rotation ?? 0;
    const commonProps = {
      stroke: shape.color,
      strokeWidth: isSelected ? 6 : 4,
      lineCap: 'round',
      lineJoin: 'round',
      rotation: rot,
      draggable: isDraggable,
      id: shape.id,
      onClick: () => handleShapeClick(shape.id),
      onTap: () => handleShapeClick(shape.id),
      ref: isSelected ? selectedShapeRef : undefined,
      ...(isDraggable && {
        onDragMove: (e: any) => handleShapeDragMove(e, shape.id),
        onDragEnd: (e: any) => handleShapeDragEnd(e, shape.id),
      })
    } as const;

    switch (shape.tool) {
      case 'line':
        return <Line key={shape.id} {...commonProps} points={denormShape.points} tension={0.5} />;
      case 'rect':
        return (
          <Rect
            key={shape.id}
            {...commonProps}
            x={denormShape.points[0]}
            y={denormShape.points[1]}
            width={denormShape.points[2] - denormShape.points[0]}
            height={denormShape.points[3] - denormShape.points[1]}
          />
        );
      case 'arrow':
         return (
          <Arrow
            key={shape.id}
            {...commonProps}
            points={denormShape.points}
            pointerLength={10}
            pointerWidth={10}
          />
        );
      case 'circle': {
        const [startX, startY, endX, endY] = denormShape.points;
        const radius = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
        return <Circle key={shape.id} {...commonProps} x={startX} y={startY} radius={radius} />;
      }
      case 'text':
        return (
          <Text
            key={shape.id}
            {...commonProps}
            x={denormShape.points[0]}
            y={denormShape.points[1]}
            text={shape.text}
            fontSize={shape.fontSize ?? 24}
            fill={shape.color}
            strokeWidth={0} // No stroke for text
          />
        );
      default:
        return null;
    }
  };

  const commitInlineText = () => {
    if (!inlineTextStagePos) return;
    const text = inlineTextDraft.trim();
    if (!text) {
      setInlineTextStagePos(null);
      setInlineTextDraft('');
      return;
    }

    const nx = inlineTextStagePos.x / stageSize.width;
    const ny = inlineTextStagePos.y / stageSize.height;
    const newShape: Shape = {
      id: `shape-${Date.now()}`,
      tool: 'text',
      points: [nx, ny],
      color,
      text,
    };
    onShapesChange([...shapes, newShape]);
    onDrawingComplete?.();
    setInlineTextStagePos(null);
    setInlineTextDraft('');
  };

  const wrapEl = wrapRef.current;
  const wrapW = wrapEl?.clientWidth ?? stageSize.width;
  const wrapH = wrapEl?.clientHeight ?? stageSize.height;
  const stageOffsetX = Math.max(0, (wrapW - stageSize.width) / 2);
  const stageOffsetY = Math.max(0, (wrapH - stageSize.height) / 2);
  const inlineTextViewPos = inlineTextStagePos
    ? {
        left: stageOffsetX + inlineTextStagePos.x,
        top: stageOffsetY + inlineTextStagePos.y,
      }
    : null;

  const textToolCursor = drawingInteractionActive && tool === 'text';
  const stageStyle: React.CSSProperties = {
    touchAction: 'none',
    pointerEvents: drawingInteractionActive ? 'auto' : 'none',
    ...(textToolCursor ? { cursor: 'text' as const } : {}),
  };

  return (
    <div
      ref={wrapRef}
      className={styles.konvaWrap}
      style={textToolCursor ? { cursor: 'text' } : undefined}
    >
      <Stage
        width={stageSize.width}
        height={stageSize.height}
        listening={drawingInteractionActive}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className={styles.konvaStage}
        style={stageStyle}
      >
        <Layer>
          {shapes.map(renderShape)}
          {tool === 'select' && selectedShapeId && drawingInteractionActive && (
            <Transformer
              ref={transformerRef}
              listening
              rotateEnabled
              /* İnce çizgide bbox yüksekliği ~stroke; padding ile min boyut ve dönüş tutamacı alanı artar */
              padding={16}
              boundBoxFunc={(_oldBox, newBox) => {
                const w = newBox.width;
                const h = newBox.height;
                if (w < 1 || h < 1) {
                  return _oldBox;
                }
                // Gerçekten degener (nokta civarı) — dışarı taşan döndürme AABB’sini reddetme
                const minSide = Math.min(w, h);
                const maxSide = Math.max(w, h);
                if (minSide < 2 && maxSide < 6) {
                  return _oldBox;
                }
                // Sahne sınırı KULLANMA: döndürmede AABB köşegenle büyür; taşınca dönüş kesiliyordu
                return newBox;
              }}
              onTransformEnd={handleTransformEnd}
            />
          )}
        </Layer>
      </Stage>
      {inlineTextViewPos && tool === 'text' && drawingInteractionActive ? (
        <textarea
          ref={textInputRef}
          className={styles.inlineTextEditor}
          style={{
            left: inlineTextViewPos.left,
            top: inlineTextViewPos.top,
            color,
            caretColor: color,
          }}
          value={inlineTextDraft}
          onChange={(e) => setInlineTextDraft(e.target.value)}
          onBlur={commitInlineText}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setInlineTextStagePos(null);
              setInlineTextDraft('');
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              commitInlineText();
            }
          }}
          rows={1}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          aria-label="Metin"
        />
      ) : null}
    </div>
  );
};

export default DrawingCanvas; 